import React, { useMemo } from 'react';
import { Animated, StyleSheet, View, Platform } from 'react-native';
import * as Device from 'expo-device';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import { useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { getShadowThemeStyles } from '@/constants/Colors';
import useTranslation from '@/hooks/useTranslation';

// : todo : get ios version

const createStyles = (isXS, chatInputStyle, messageWidth, showDisclaimer, colors, themeStyles, bottomInset) => {

  const customStyles = {
    inputContainer: {
      paddingHorizontal: 15,
      height: 'auto',
    },
    inputWrapper: {
      maxWidth: '100%',
      paddingBottom: 0,
      height: 'auto',
  },
    disclaimerContainer: {
      backgroundColor: 'transparent',
    },
  };

  if (showDisclaimer) {
    if (chatInputStyle === CHAT_INPUT_STYLE.FLOATING) {
      customStyles.inputContainer.paddingHorizontal = 15;
    } else if (chatInputStyle === CHAT_INPUT_STYLE.FIXED) {
      customStyles.disclaimerContainer.backgroundColor = Platform.OS === 'macos' ? colors.solid_color_block : themeStyles.backgroundColor;
      if (Platform.OS === 'web') {
        customStyles.disclaimerContainer.backdropFilter = 'blur(18px) saturate(1.8)';
        customStyles.disclaimerContainer.WebkitBackdropFilter = 'blur(18px) saturate(1.8)';
      }
      customStyles.disclaimerContainer.paddingHorizontal = 0;
      customStyles.disclaimerContainer.borderTopWidth = 1;
      customStyles.disclaimerContainer.borderColor = themeStyles.borderColor;
    } else {
      customStyles.inputContainer.paddingHorizontal = 0;
    }
  } else {
    if (isXS) {
      customStyles.inputContainer.paddingHorizontal = 0;
    }
  }

  if (chatInputStyle === CHAT_INPUT_STYLE.DETAILED) {
    customStyles.inputContainer.paddingHorizontal = 0;
    customStyles.inputContainer.height = '100%';
    customStyles.inputWrapper.height = '100%';
  }

  if (chatInputStyle === CHAT_INPUT_STYLE.FIXED) {
    customStyles.inputContainer.paddingHorizontal = 0;
  }

  if (chatInputStyle === CHAT_INPUT_STYLE.FLOATING) {
    if (messageWidth === MESSAGE_WIDTH.NARROW) {
      customStyles.inputWrapper.maxWidth = MESSAGE_WIDTH_AMOUNTS.NARROW;
    } else if (messageWidth === MESSAGE_WIDTH.MEDIUM) {
      customStyles.inputWrapper.maxWidth = MESSAGE_WIDTH_AMOUNTS.MEDIUM;
    } 
  }

  return StyleSheet.create({
    inputContainer: {
      width: '100%',
      position: 'relative',
      ...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
      alignItems: 'center',
      backgroundColor: isXS ? colors.solid_color_primary : 'transparent',
      zIndex: 10,
      bottom: 0,
      left: 0,
      right: 0,
      paddingHorizontal: customStyles.inputContainer.paddingHorizontal,
      height: customStyles.inputContainer.height,
    },
    inputWrapper: {
      width: '100%',
      maxWidth: customStyles.inputWrapper.maxWidth,
      height: customStyles.inputWrapper.height,
      paddingVertical: 0,
    },
    disclaimerContainer: {
      width: '100%',
      paddingHorizontal: 15,
      paddingTop: 0,
      paddingBottom: 8,
      alignItems: 'center',
      justifyContent: 'center',
      ...customStyles.disclaimerContainer,
    },
    disclaimerText: {
      textAlign: 'center',
    }
  });
};

/**
 * @param {Object} [wrapperAnimatedStyle] Animated style applied to the input
 *   wrapper itself. Used by the chat → Work handover to fade the composer
 *   through zero while its controls change (see useWorkModeHandover). It goes
 *   on the existing wrapper rather than a new node so the DETAILED layout,
 *   which relies on this View filling its parent, is unaffected.
 */
function ChatInputContainer({
  children,
  forceInputStyle,
  wrapperAnimatedStyle,
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const deviceType = Device.deviceType;
  const isMobileOrTablet = deviceType === Device.DeviceType.PHONE || deviceType === Device.DeviceType.TABLET;
  const themeStyles = getShadowThemeStyles(theme);
  const storeInputStyle = useUIStore(state => state.chatInputStyle);
  const messageWidth = useUIStore(state => state.messageWidth);
  const insets = useSafeAreaInsets();
  const chatInputStyle = useMemo(() => {
    return forceInputStyle || storeInputStyle;
  }, [forceInputStyle, storeInputStyle]);
  const showDisclaimer = useMemo(() => {
    return isXS || isMobileOrTablet;
  }, [isXS, isMobileOrTablet]);
  const disclaimer = t('chatInput.disclaimer');

  const styles = React.useMemo(() => createStyles(isXS, chatInputStyle, messageWidth, showDisclaimer, colors, themeStyles, insets.bottom), 
    [isXS, chatInputStyle, messageWidth, showDisclaimer, colors, themeStyles, insets.bottom]);

  return (
    <View style={styles.inputContainer}>
      <Animated.View style={[styles.inputWrapper, wrapperAnimatedStyle]}>
        {children}
      </Animated.View>
      {showDisclaimer && (
        <View style={styles.disclaimerContainer}>
          <Text color='secondary' size='xs' style={styles.disclaimerText}>
            {disclaimer}
          </Text>
        </View>
      )}
    </View>
  )

}

export default ChatInputContainer;